import { useEffect, useState } from "react";
import { parseNoteMetadata, isValidNote, type Note } from "../lib/note";
import { assetIdLabel } from "../lib/pools";

const STORAGE_KEY = "tonado.notes.v1";

type StoredNote = {
  raw: string;
  savedAt: number;
};

function load(): StoredNote[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as StoredNote[];
  } catch {
    return [];
  }
}

function save(rows: StoredNote[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

export function Notes() {
  const [rows, setRows] = useState<StoredNote[]>([]);
  const [input, setInput] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setRows(load());
  }, []);

  function add() {
    setErr(null);
    const trimmed = input.trim();
    if (!isValidNote(trimmed)) {
      setErr("Invalid note format. Expected: tonado-<asset>-<amount>-<network>-0x<124-hex>");
      return;
    }
    const next = [{ raw: trimmed, savedAt: Date.now() }, ...rows.filter((r) => r.raw !== trimmed)];
    setRows(next);
    save(next);
    setInput("");
  }

  function remove(raw: string) {
    const next = rows.filter((r) => r.raw !== raw);
    setRows(next);
    save(next);
  }

  return (
    <div className="flow flow--notes">
      <div className="notes-intro">
        <p className="kicker">
          <span className="kicker-dot" /> Local-only · never leaves this device
        </p>
        <h2 className="notes-intro__title">
          Your notes, <em>encrypted-to-self</em>.
        </h2>
        <p className="notes-intro__lede">
          Stored in this browser's <code>localStorage</code>. Clear the cache and they're gone -
          back them up off-device too. The relayer never sees these strings.
        </p>
      </div>

      <label className="field">
        <span className="field__label">Add a note</span>
        <textarea
          rows={2}
          placeholder="tonado-ton-10-mainnet-…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
        />
        <span className="field__hint">
          {err ? <span className="field__error">{err}</span> : "Stored only in this browser."}
        </span>
      </label>
      <div className="cta-row">
        <button type="button" className="btn btn--primary" disabled={!input.trim()} onClick={add}>
          Save note
        </button>
      </div>

      {rows.length > 0 ? (
        <ul className="notes-list">
          {rows.map((r) => {
            const note = safeParse(r.raw);
            return (
              <li key={r.raw}>
                <div>
                  <p className="notes-list__head">
                    {note ? (
                      <>
                        <span className="notes-list__chip">
                          {note.amount} {assetIdLabel(note.asset)}
                        </span>
                        <span className="notes-list__chip notes-list__chip--alt">
                          {note.network}
                        </span>
                      </>
                    ) : (
                      <span className="notes-list__chip">unparsed</span>
                    )}
                    <span className="notes-list__date">
                      saved {new Date(r.savedAt).toLocaleString()}
                    </span>
                  </p>
                  <p className="notes-list__raw mono mono--break">{r.raw}</p>
                </div>
                <div className="notes-list__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => navigator.clipboard.writeText(r.raw)}
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm btn--danger"
                    onClick={() => remove(r.raw)}
                  >
                    Forget
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="empty">
          No notes saved yet. Saved notes are stored locally and never transmitted.
        </p>
      )}
    </div>
  );
}

function safeParse(s: string): Note | null {
  return parseNoteMetadata(s);
}
